import Phaser from "phaser";
import { MAP_SIZE, worldToScreen, type ScreenPos } from "../map/Iso";
import type { CollisionMap } from "../map/CollisionMap";
import type { OccupancyMap } from "../map/OccupancyMap";

const LOCAL_SHEEP_COUNT = 3;
const NPC_WALK_SPEED = 1.5;   // tiles / sec — matches server
const NPC_RETARGET = 0.3;
const NPC_IDLE_MIN_MS = 1_500;
const NPC_IDLE_MAX_MS = 4_000;
const MAX_WANDER_ATTEMPTS = 20;

const SHEEP_TEX = "sheep-atlas";
const FRAME_W = 32;
const FRAME_H = 32;
const FRAMES = 8;

export interface SheepDebugItem {
  id: string;
  mode: "local-wander" | "server-lerp";
  pos: string;
  target: string;
  idleMs?: number | undefined;
}

/**
 * Manages server-authoritative NPC sheep sprites.
 * Positions come from the server via snapshots and npc_moved events;
 * the client only interpolates for smooth rendering.
 */
export class SheepNPC {
  private scene: Phaser.Scene;
  private origin: ScreenPos;
  private npcs = new Map<string, NPCSheepSprite>();
  private collision?: CollisionMap | undefined;
  private occupancy?: OccupancyMap | undefined;

  constructor(scene: Phaser.Scene, origin: ScreenPos, collision?: CollisionMap, occupancy?: OccupancyMap) {
    this.scene = scene;
    this.origin = origin;
    this.collision = collision;
    this.occupancy = occupancy;
    ensureSheepTexture(scene);
  }

  /** Apply a full snapshot — create/update all NPCs, remove stale ones. */
  syncSnapshot(npcs: { id: string; x: number; y: number; dir: number }[]): void {
    // When the server provides real NPCs, drop local fallback sheep.
    if (npcs.length > 0) {
      const localIds = [...this.npcs.keys()].filter((id) => id.startsWith("__local_"));
      for (const id of localIds) {
        this.npcs.get(id)!.destroy();
        this.npcs.delete(id);
        this.occupancy?.release(id);
      }
    }

    const seen = new Set<string>();
    for (const npc of npcs) {
      seen.add(npc.id);
      this.upsert(npc.id, npc.x, npc.y, npc.dir);
    }
    const toRemove: string[] = [];
    for (const [id] of this.npcs) {
      if (!id.startsWith("__local_") && !seen.has(id)) toRemove.push(id);
    }
    for (const id of toRemove) {
      this.npcs.get(id)!.destroy();
      this.npcs.delete(id);
    }
  }

  /** Update a single NPC from a npc_moved event. */
  upsert(id: string, x: number, y: number, dir: number): void {
    let sprite = this.npcs.get(id);
    if (!sprite) {
      sprite = new NPCSheepSprite(this.scene, this.origin, x, y, dir, id, this.collision, this.occupancy);
      this.npcs.set(id, sprite);
    } else {
      sprite.setTarget(x, y, dir);
    }
    this.occupancy?.claim(id, x, y);
  }

  /** Interpolate towards server positions each frame. */
  tick(dtMs: number): void {
    // If the server hasn't sent any NPCs yet, spawn local wandering sheep.
    if (this.npcs.size === 0) {
      for (let i = 0; i < LOCAL_SHEEP_COUNT; i++) {
        const id = `__local_${i}__`;
        const pos = this.findWalkableSpawn();
        const sprite = new NPCSheepSprite(this.scene, this.origin, pos.x, pos.y, 0, id, this.collision, this.occupancy);
        sprite.startLocalWander();
        this.npcs.set(id, sprite);
        this.occupancy?.claim(id, pos.x, pos.y);
      }
    }
    for (const sprite of this.npcs.values()) {
      sprite.tick(dtMs);
    }
  }

  /** Find a random walkable, unoccupied spawn position. */
  private findWalkableSpawn(): { x: number; y: number } {
    const margin = 2;
    for (let i = 0; i < MAX_WANDER_ATTEMPTS; i++) {
      const x = Math.random() * (MAP_SIZE - margin * 2) + margin;
      const y = Math.random() * (MAP_SIZE - margin * 2) + margin;
      if (this.collision?.isBlocked(x, y)) continue;
      if (this.occupancy?.isOccupied(x, y)) continue;
      return { x, y };
    }
    // Fallback
    const mid = MAP_SIZE / 2;
    return { x: mid, y: mid };
  }

  getDebugStatus(): SheepDebugItem[] {
    return [...this.npcs.entries()].map(([id, sprite]) => sprite.getDebugInfo(id));
  }

  destroy(): void {
    for (const [id, sprite] of this.npcs) {
      sprite.destroy();
      this.occupancy?.release(id);
    }
    this.npcs.clear();
  }
}

/** Single sheep sprite with lerp interpolation. */
class NPCSheepSprite {
  private container: Phaser.GameObjects.Container;
  private body: Phaser.GameObjects.Sprite;
  private origin: ScreenPos;
  private id: string;
  private collision?: CollisionMap | undefined;
  private occupancy?: OccupancyMap | undefined;

  private x: number;
  private y: number;
  private dir: number;

  // Server-authoritative target (lerp destination)
  private tx: number;
  private ty: number;

  // Local wander state (used when server is absent)
  private localMode = false;
  private wanderTx = 0;
  private wanderTy = 0;
  private wanderIdleMs = 0;

  constructor(scene: Phaser.Scene, origin: ScreenPos, x: number, y: number, dir: number, id: string, collision?: CollisionMap, occupancy?: OccupancyMap) {
    this.origin = origin;
    this.id = id;
    this.collision = collision;
    this.occupancy = occupancy;
    this.x = x;
    this.y = y;
    this.tx = x;
    this.ty = y;
    this.dir = dir;

    const { sx, sy } = worldToScreen(x, y);

    this.body = scene.add.sprite(0, 0, SHEEP_TEX, `d${dir}`);
    this.body.setOrigin(0.5, 1);
    this.body.setScale(0.7);

    const label = scene.add.text(0, -26, "🐑", { fontSize: "10px" });
    label.setOrigin(0.5, 0.5);

    this.container = scene.add.container(origin.sx + sx, origin.sy + sy, [this.body, label]);
    this.container.setDepth(this.depth());
  }

  /** Activate client-side wandering AI (used when no server data is available). */
  startLocalWander(): void {
    this.localMode = true;
    this.pickWanderTarget();
    this.wanderIdleMs = 0; // start walking immediately
  }

  setTarget(x: number, y: number, dir: number): void {
    this.tx = x;
    this.ty = y;
    this.dir = dir;
    this.body.setFrame(`d${dir}`);
  }

  tick(dtMs: number): void {
    if (this.localMode) {
      this.tickLocalWander(dtMs);
    } else {
      // Lerp towards server position for smooth movement
      const lerpFactor = Math.min(1, dtMs * 0.008);
      this.x += (this.tx - this.x) * lerpFactor;
      this.y += (this.ty - this.y) * lerpFactor;
    }

    const screen = worldToScreen(this.x, this.y);
    this.container.x = this.origin.sx + screen.sx;
    this.container.y = this.origin.sy + screen.sy;
    this.container.setDepth(this.depth());
  }

  private tickLocalWander(dtMs: number): void {
    const dx = this.wanderTx - this.x;
    const dy = this.wanderTy - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist < NPC_RETARGET) {
      this.wanderIdleMs -= dtMs;
      if (this.wanderIdleMs <= 0) {
        this.pickWanderTarget();
        this.wanderIdleMs = NPC_IDLE_MIN_MS + Math.random() * (NPC_IDLE_MAX_MS - NPC_IDLE_MIN_MS);
      }
    } else {
      const step = Math.min(NPC_WALK_SPEED * dtMs / 1_000, dist);
      const nx = this.x + (dx / dist) * step;
      const ny = this.y + (dy / dist) * step;

      // Check hard collision
      if (this.collision?.isBlocked(nx, ny)) {
        this.pickWanderTarget();
        return;
      }
      // Check soft collision (other NPCs)
      if (this.occupancy && !this.occupancy.canMoveTo(this.id, nx, ny)) {
        this.pickWanderTarget();
        return;
      }

      this.x = nx;
      this.y = ny;
      this.occupancy?.claim(this.id, this.x, this.y);
      const angle = Math.atan2(dy, dx);
      const dir = Math.round((angle / (Math.PI / 4)) + 8) % 8;
      this.body.setFrame(`d${dir}`);
    }
  }

  /** Pick a random walkable, unoccupied wander target. */
  private pickWanderTarget(): void {
    const margin = 2;
    for (let i = 0; i < MAX_WANDER_ATTEMPTS; i++) {
      const x = Math.random() * (MAP_SIZE - margin * 2) + margin;
      const y = Math.random() * (MAP_SIZE - margin * 2) + margin;
      if (this.collision?.isBlocked(x, y)) continue;
      this.wanderTx = x;
      this.wanderTy = y;
      return;
    }
    // Fallback: wander toward centre
    this.wanderTx = MAP_SIZE / 2 + (Math.random() - 0.5) * 2;
    this.wanderTy = MAP_SIZE / 2 + (Math.random() - 0.5) * 2;
  }

  destroy(): void {
    this.container.destroy();
  }

  getDebugInfo(id: string): SheepDebugItem {
    const fmt = (v: number) => v.toFixed(1);
    if (this.localMode) {
      const dx = this.wanderTx - this.x;
      const dy = this.wanderTy - this.y;
      const moving = Math.sqrt(dx * dx + dy * dy) >= NPC_RETARGET;
      return {
        id: id.startsWith("__local_") ? id.replace(/_/g, "").replace("local", "local-") : id.slice(0, 8),
        mode: "local-wander",
        pos: `${fmt(this.x)},${fmt(this.y)}`,
        target: moving ? `→${fmt(this.wanderTx)},${fmt(this.wanderTy)}` : "idle",
        idleMs: moving ? undefined : Math.round(this.wanderIdleMs),
      };
    }
    return {
      id: id.slice(0, 8),
      mode: "server-lerp",
      pos: `${fmt(this.x)},${fmt(this.y)}`,
      target: `→${fmt(this.tx)},${fmt(this.ty)}`,
    };
  }

  private depth(): number {
    return (this.x + this.y) * 1000;
  }
}

// ── Procedural sheep texture ───────────────────────────────────────────

function ensureSheepTexture(scene: Phaser.Scene): void {
  if (scene.textures.exists(SHEEP_TEX)) return;

  const totalW = FRAME_W * FRAMES;
  const canvas = scene.textures.createCanvas(SHEEP_TEX, totalW, FRAME_H);
  if (!canvas) throw new Error("createCanvas failed for sheep");

  const ctx = canvas.context;

  for (let i = 0; i < FRAMES; i++) {
    drawSheepFrame(ctx, i, FRAME_W * i);
  }

  canvas.refresh();

  for (let i = 0; i < FRAMES; i++) {
    canvas.add(`d${i}`, 0, FRAME_W * i, 0, FRAME_W, FRAME_H);
  }
}

function drawSheepFrame(ctx: CanvasRenderingContext2D, frame: number, ox: number): void {
  const cx = ox + FRAME_W / 2;
  const feetY = FRAME_H - 3;

  ctx.save();

  // Shadow
  ctx.fillStyle = "rgba(0,0,0,0.25)";
  ctx.beginPath();
  ctx.ellipse(cx, feetY, 9, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  // Legs (dark)
  ctx.fillStyle = "#3a3028";
  const legOff = 4;
  ctx.fillRect(cx - legOff - 1.5, feetY - 8, 3, 8);
  ctx.fillRect(cx + legOff - 1.5, feetY - 8, 3, 8);

  // Woolly body — big fluffy ellipse
  ctx.fillStyle = "#f2efe6";
  ctx.strokeStyle = "#d0cdc4";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, feetY - 14, 10, 8, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Wool tufts (small bumps around the body)
  ctx.fillStyle = "#eae7de";
  for (let a = 0; a < 5; a++) {
    const angle = (a / 5) * Math.PI * 2 - Math.PI / 2;
    const bx = cx + Math.cos(angle) * 8;
    const by = feetY - 14 + Math.sin(angle) * 6;
    ctx.beginPath();
    ctx.arc(bx, by, 3.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Head — direction-aware offset
  const dirAngle = (frame * Math.PI) / 4;
  const headDx = Math.cos(dirAngle) * 7;
  const headDy = Math.sin(dirAngle) * 4;
  const headX = cx + headDx;
  const headY = feetY - 20 + headDy;

  // Face (dark)
  ctx.fillStyle = "#3a3028";
  ctx.strokeStyle = "#2a2018";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(headX, headY, 4, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Ears
  ctx.fillStyle = "#3a3028";
  ctx.beginPath();
  ctx.ellipse(headX - 4, headY - 2, 2.5, 1.5, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(headX + 4, headY - 2, 2.5, 1.5, 0.4, 0, Math.PI * 2);
  ctx.fill();

  // Eyes (tiny white dots)
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(headX - 1.5, headY - 1, 1, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(headX + 1.5, headY - 1, 1, 0, Math.PI * 2);
  ctx.fill();

  ctx.restore();
}
